import { useEffect, useRef } from 'react';
import { View, Text, StyleSheet, Animated } from 'react-native';
import { theme } from '@/constants/theme';
import { KursiAvatar } from './KursiAvatar';
import { useAccessibility } from '@/contexts/AccessibilityContext';

interface TypingIndicatorProps {
  message?:    string;
  showAvatar?: boolean;
}

const VIOLET = '#7C3AED';
const BG     = '#1E1B3A';
const BORDER = 'rgba(124, 58, 237, 0.35)';

export function TypingIndicator({ message, showAvatar = true }: TypingIndicatorProps) {
  const { settings } = useAccessibility();
  const dot1     = useRef(new Animated.Value(0)).current;
  const dot2     = useRef(new Animated.Value(0)).current;
  const dot3     = useRef(new Animated.Value(0)).current;
  const glowAnim = useRef(new Animated.Value(0.5)).current;

  useEffect(() => {
    if (settings.stopAnimations) return;
    const makeDot = (val: Animated.Value, delay: number) =>
      Animated.loop(Animated.sequence([
        Animated.delay(delay),
        Animated.spring(val, { toValue: -8, tension: 130, friction: 6, useNativeDriver: true }),
        Animated.spring(val, { toValue:  0, tension: 130, friction: 6, useNativeDriver: true }),
        Animated.delay(380),
      ]));
    const glow = Animated.loop(Animated.sequence([
      Animated.timing(glowAnim, { toValue: 1,   duration: 850, useNativeDriver: true }),
      Animated.timing(glowAnim, { toValue: 0.5, duration: 850, useNativeDriver: true }),
    ]));
    const dots = Animated.parallel([makeDot(dot1, 0), makeDot(dot2, 150), makeDot(dot3, 300)]);
    dots.start(); glow.start();
    return () => { dots.stop(); glow.stop(); };
  }, [dot1, dot2, dot3, glowAnim, settings.stopAnimations]);

  if (message) {
    return (
      <View style={styles.container} accessibilityLiveRegion="polite" accessibilityLabel={message} accessible>
        {showAvatar && <KursiAvatar />}
        <View style={[styles.thinkRow, styles.stableH]}>
          <Animated.View style={[styles.miniDots, { opacity: glowAnim }]}>
            {[dot1, dot2, dot3].map((d, i) => (
              <Animated.View key={i} style={[styles.miniDot, { transform: [{ translateY: d }] }]} />
            ))}
          </Animated.View>
          <Text style={styles.thinkText}>{message}</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container} accessibilityLiveRegion="polite" accessibilityLabel="Kursi כותבת תשובה" accessible>
      {showAvatar && <KursiAvatar />}
      <View style={[styles.bubble, styles.stableH]} aria-hidden>
        <Animated.View style={[styles.dot, styles.d1, { transform: [{ translateY: dot1 }] }]} />
        <Animated.View style={[styles.dot, styles.d2, { transform: [{ translateY: dot2 }] }]} />
        <Animated.View style={[styles.dot, styles.d3, { transform: [{ translateY: dot3 }] }]} />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flexDirection: 'row-reverse', marginVertical: 6, paddingHorizontal: 14, justifyContent: 'flex-start', gap: 10 },
  stableH:   { minHeight: 44 },
  bubble: {
    flexDirection: 'row', alignItems: 'center', gap: 7,
    backgroundColor: BG, paddingVertical: 14, paddingHorizontal: 18,
    borderRadius: 20, borderBottomLeftRadius: 5,  // ✅ תוקן: פינה מחודדת בצד שמאל (צד סוכן)
    borderWidth: 1.5, borderColor: BORDER,
    shadowColor: VIOLET, shadowOffset: { width: 0, height: 3 }, shadowOpacity: 0.22, shadowRadius: 8, elevation: 4,
  },
  dot: { width: 9, height: 9, borderRadius: 5 },
  d1:  { backgroundColor: '#7C3AED' },
  d2:  { backgroundColor: '#8B5CF6' },
  d3:  { backgroundColor: '#A78BFA' },
  thinkRow:  { flexDirection: 'row-reverse', alignItems: 'center', gap: 8, paddingVertical: 4 },
  miniDots:  { flexDirection: 'row', gap: 4, alignItems: 'center' },
  miniDot:   { width: 5, height: 5, borderRadius: 3, backgroundColor: 'rgba(124,58,237,0.50)' },
  thinkText: { fontSize: 14, fontFamily: theme.fonts.regular, color: 'rgba(255,255,255,0.40)', textAlign: 'right', writingDirection: 'rtl', flex: 1 },
});